// Βοηθητικά για το «σκορ υγείας» (0-100) των προϊόντων.
// Το σκορ αποθηκεύεται στο products.health_score και αναλυτικά στοιχεία
// στον πίνακα product_nutrition.

export type HealthBand = {
  key: "great" | "good" | "ok" | "low" | "bad";
  label: string;
  /** Κλάσεις για κύκλο/πλαίσιο (φόντο + κείμενο + περίγραμμα). */
  circleClass: string;
  panelClass: string;
  barClass: string;
  emoji: string;
  /** Έξυπνα σχόλια — επιλέγεται σταθερά με βάση το σκορ. */
  comments: string[];
};

const BANDS: HealthBand[] = [
  {
    key: "great",
    label: "Εξαιρετικό για την υγεία",
    circleClass: "bg-emerald-500 text-white ring-emerald-200",
    panelClass: "border-emerald-300 bg-emerald-50 text-emerald-900",
    barClass: "bg-emerald-500",
    emoji: "🥦",
    comments: [
      "Ο πάντα χειροκροτεί! Από τις πιο έξυπνες επιλογές του ραφιού.",
      "Πράσινο φως: βάλ' το άφοβα στο καλάθι.",
      "Θρεπτικό και ελαφρύ — το σώμα σου θα σε ευχαριστήσει.",
    ],
  },
  {
    key: "good",
    label: "Καλή επιλογή",
    circleClass: "bg-lime-500 text-white ring-lime-200",
    panelClass: "border-lime-300 bg-lime-50 text-lime-900",
    barClass: "bg-lime-500",
    emoji: "🌿",
    comments: [
      "Καλό σκορ! Ισορροπημένη επιλογή για την καθημερινότητα.",
      "Στέκει άνετα στο τραπέζι σου — χωρίς ενοχές.",
      "Ωραία ισορροπία θρεπτικών· ο πάντα το εγκρίνει.",
    ],
  },
  {
    key: "ok",
    label: "Μέτρια επιλογή",
    circleClass: "bg-amber-400 text-amber-950 ring-amber-200",
    panelClass: "border-amber-300 bg-amber-50 text-amber-900",
    barClass: "bg-amber-400",
    emoji: "🙂",
    comments: [
      "Ούτε ήρωας ούτε κακός — με μέτρο είναι μια χαρά.",
      "Καλό για πού και πού, όχι για κάθε μέρα.",
      "Μέτρια επίδοση: κράτα μικρές μερίδες.",
    ],
  },
  {
    key: "low",
    label: "Χαμηλό σκορ",
    circleClass: "bg-orange-500 text-white ring-orange-200",
    panelClass: "border-orange-300 bg-orange-50 text-orange-900",
    barClass: "bg-orange-500",
    emoji: "⚠️",
    comments: [
      "Λίγο βαρύ… καλύτερα σαν περιστασιακή απόλαυση.",
      "Ο πάντα σηκώνει φρύδι: κράτα το για τις γιορτές.",
      "Πλούσιο σε ό,τι θέλουμε με μέτρο — μικρή μερίδα.",
    ],
  },
  {
    key: "bad",
    label: "Απόλαυση με μέτρο",
    circleClass: "bg-red-500 text-white ring-red-200",
    panelClass: "border-red-300 bg-red-50 text-red-900",
    barClass: "bg-red-500",
    emoji: "🍭",
    comments: [
      "Καθαρή απόλαυση! Νόστιμο ναι, υγιεινό… όχι πολύ.",
      "Το ένοχο γλυκό της παρέας — σπάνια και με χαμόγελο.",
      "Ο πάντα λέει: μια φορά στο τόσο, όλα επιτρέπονται.",
    ],
  },
];

export function healthBand(score: number): HealthBand {
  if (score >= 80) return BANDS[0]!;
  if (score >= 60) return BANDS[1]!;
  if (score >= 40) return BANDS[2]!;
  if (score >= 20) return BANDS[3]!;
  return BANDS[4]!;
}

export function healthComment(score: number): string {
  const band = healthBand(score);
  return band.comments[score % band.comments.length]!;
}

export const HEALTH_FILTERS = [
  { key: "great", label: "🥦 Εξαιρετικό (80+)", min: 80 },
  { key: "good", label: "🌿 Καλό (60+)", min: 60 },
  { key: "ok", label: "🙂 Μέτριο και πάνω (40+)", min: 40 },
] as const;

export type HealthFilterKey = (typeof HEALTH_FILTERS)[number]["key"];

export function healthFilterMin(key: string | undefined): number | undefined {
  return HEALTH_FILTERS.find((f) => f.key === key)?.min;
}
